import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import * as itineraryRepo from "../repo/itineraryRepo";
import type {
  ItineraryInternalInteractions,
  ItineraryInternalMarkResult,
  ItineraryMarkType,
  ItinerarySuggestion,
} from "../types";

export type ItineraryInternalInteractionRepositories = {
  load: (itineraryId: string) => Promise<ItineraryInternalInteractions>;
  toggle: (
    itineraryId: string,
    markType: ItineraryMarkType,
  ) => Promise<ItineraryInternalMarkResult>;
  createSuggestion: (itineraryId: string, content: string, parentId?: string | null) => Promise<ItinerarySuggestion>;
};

const defaultRepositories: ItineraryInternalInteractionRepositories = {
  load: (itineraryId) => itineraryRepo.getInternalInteractions(itineraryId),
  toggle: (itineraryId, markType) => itineraryRepo.toggleInternalMark(itineraryId, markType),
  createSuggestion: (itineraryId, content, parentId) => itineraryRepo.createItinerarySuggestion(
    itineraryId,
    content,
    parentId,
  ),
};

/** 管理内部建议、拉黑和收藏的服务端状态与精确缓存更新。 */
export function useItineraryInternalInteractions(
  itineraryId: string,
  enabled: boolean,
  repositories: ItineraryInternalInteractionRepositories = defaultRepositories,
) {
  const queryClient = useQueryClient();
  const queryKey = ["itinerary", "internal-interactions", itineraryId] as const;
  const query = useQuery({
    queryKey,
    enabled: enabled && Boolean(itineraryId),
    queryFn: () => repositories.load(itineraryId),
    retry: false,
  });
  const toggleMutation = useMutation({
    mutationFn: (markType: ItineraryMarkType) => repositories.toggle(itineraryId, markType),
    onSuccess: (result) => {
      queryClient.setQueryData<ItineraryInternalInteractions>(queryKey, (current) => {
        if (!current) return current;
        return result.markType === "blacklist"
          ? {
              ...current,
              blacklistCount: result.total,
              blacklistedByMe: result.selected,
            }
          : {
              ...current,
              favoriteCount: result.total,
              favoritedByMe: result.selected,
            };
      });
      void queryClient.invalidateQueries({ queryKey, refetchType: "none" });
    },
  });
  const suggestionMutation = useMutation({
    mutationFn: ({ content, parentId }: { content: string; parentId?: string | null }) => (
      repositories.createSuggestion(itineraryId, content, parentId)
    ),
    onSuccess: (suggestion) => {
      queryClient.setQueryData<ItineraryInternalInteractions>(queryKey, (current) => current
        ? {
            ...current,
            suggestionCount: current.suggestionCount + 1,
            suggestions: [...current.suggestions, suggestion],
          }
        : current);
      void queryClient.invalidateQueries({ queryKey, refetchType: "none" });
    },
  });

  return {
    interactions: query.data,
    loading: query.isLoading,
    loadError: query.error,
    reload: query.refetch,
    toggle: toggleMutation.mutateAsync,
    pendingMark: toggleMutation.isPending ? toggleMutation.variables : undefined,
    toggleError: toggleMutation.error,
    createSuggestion: (content: string, parentId?: string | null) => suggestionMutation.mutateAsync({
      content,
      parentId,
    }),
    submittingSuggestion: suggestionMutation.isPending,
    suggestionError: suggestionMutation.error,
  };
}
